/* eslint-disable */
import React, { forwardRef, useRef, useEffect, useImperativeHandle, Children, memo } from 'react';
import cls from 'classnames';
import htmlReactParser from 'html-react-parser';
import isArray from '@dcode/is/array/array';
import isString from '@dcode/is/string/string';
import isFunction from '@dcode/is/fn/fn';
import { FIRST_LAST, LAST_FIRST, EDGES_CENTER, CENTER_EDGES, RANDOM } from '../TextAnimMode';
import { LINES, WORDS, CHARS } from '../TextAnimSplit';
import escapeHTML from '../utils/escapeHTML';

export const TextAnimChildren = forwardRef(({
	className,
	classes,
	type,
	children,
	dangerouslySetInnerHTML,
	split,
	sort,
	debug,
	...props
}, ref) => {
	const rootRef = useRef();
	const rowsRef = useRef();
	const charsRef = useRef();
	const textRef = useRef();
	const htmlTextRef = useRef();
	let indexCharRef = 0;
	
	const setElementRef = (ref, index) => (element) => {
		ref[index] = element;
	};

	const isInline = (elementType) => {
		if (elementType === 'br' || elementType === 'hr') return true;
		return false;
	};

	const parseCharValue = (value) => (
		value === ' ' ? '&nbsp' : value
	);

	const parseRowValue = (value, strict = false) => (
		strict ? value.split('').map(parseCharValue).join('') : value
	);

	const parseChar = (value, index) => {
		switch(split) {
			case LINES: return value;
			case WORDS: return value;
			case CHARS: return parseCharValue(value);
			default: return value;
		}
	};

	const breakText = (value) => {
		switch (split) {
			case LINES: return [parseRowValue(value)];
			case WORDS: return value.split(' ').join(' &nbsp ').split(' ');
			case CHARS: return value.split('');
			default: return isFunction(split) ? split(value) || value : value;
		}
	};

	const sortChars = (value) => {
		switch (sort) {
			case FIRST_LAST: return value;
			case LAST_FIRST: return value;
			case EDGES_CENTER: return value;
			case CENTER_EDGES: return value;
			case RANDOM: return value;
			default: return isFunction(sort) ? sort(value) || value : value;
		}
	};

	const renderCharText = (value, idy, index) => {
		return (
			<span
				ref={setElementRef(charsRef.current, index)}
				key={idy}
				className={cls(classes.textAnimChildrenChar)}
				data-textanim-y={idy}
				data-textanim-char={index}
				dangerouslySetInnerHTML={{
					__html: parseChar(value, idy),
				}}>
			</span>
		);
	};

	const renderCharElement = (element, idy, index) => {
		return React.cloneElement(element, {
			ref: setElementRef(charsRef.current, index),
			key: idy,
			className: cls(classes.textAnimChildrenChar),
			['data-textanim-y']: idy,
			['data-textanim-char']: index,
		});
	};

	const renderChars = (chars, idy, index) => {
		if (isArray(chars)) return chars.map((char) => (
			renderCharText(char, idy++, indexCharRef++))
		);
		return renderCharElement(chars, idy++, indexCharRef++);
	};

	const renderRow = (chars, idx, idy, index) => {
		return (
			<span
				ref={setElementRef(rowsRef.current, index)}
				key={idx}
				className={cls(classes.textAnimChildrenRow)}
				data-textanim-x={idx}
				data-textanim-row={index}>
				{renderChars(chars, idy, index)}
			</span>
		);
	};

	const renderChildren = (elements) => {
		let idy = 0;
		let index = 0;
		return elements.map((element, idx) => {
			if (!element) return null;
			if (isString(element)) {
				const text = breakText(element.trim());
				const sortedText = sortChars(text);
				return renderRow(sortedText, idx, idy, index++);
			}
			if (isInline(element.type)) {
				idy += 1;
				return element;
			}
			return renderRow(element, idx, idy++, index++);
		});
	};

	const setInnerHTML = (dangerouslySetInnerHTML, children) => {
		charsRef.current = [];
		rowsRef.current = [];
		indexCharRef = 0;
		if (dangerouslySetInnerHTML && children) {
			throw new Error('Can only set one of `children` or `props.dangerouslySetInnerHTML`');
		} else if (dangerouslySetInnerHTML) {
			htmlTextRef.current = dangerouslySetInnerHTML.__html;
			return [].concat(htmlReactParser(dangerouslySetInnerHTML.__html));
		}
		textRef.current = children;
		return Children.toArray(escapeHTML(children));
	};

	useImperativeHandle(ref, () => ({
		element: rootRef.current,
		chars: charsRef.current,
		rows: rowsRef.current,
		htmlText: htmlTextRef.current,
		text: textRef.current,
	}));

	return React.createElement(type, {
		className: cls(classes.textAnimChildren, className),
		...props,
		'data-textanim-debug': debug || undefined,
	}, renderChildren(setInnerHTML(dangerouslySetInnerHTML, children)));
});

export default memo(TextAnimChildren);
